/**
 * The six crate lids, as one instanced mesh — four of which never move.
 *
 * ── Why the lids are not part of the crate ──────────────────────────────────
 *
 * `dressFatteningHall` instances the crates with `hen-crate-lid-pivot` removed,
 * because `instanceGroup` bakes sub-mesh transforms into the merged geometry: a
 * lid instanced with its crate is a lid welded shut. So the lids are rebuilt
 * here, in a single `InstancedMesh` whose per-instance matrices this class owns.
 *
 * **All six live in that one mesh, including the four that never open.** A
 * second instanced mesh for the static ones would be tidier to read and would
 * cost three more draw calls in a room that had thirteen of headroom before this
 * beat put Kestner in it. The four static instances are simply written once at
 * construction and never touched again.
 *
 * Which crates can open is not decided here. `BLACKOUT_OPEN_CRATES` in
 * `fattening.ts` is the one list, for the same reason `penBounds` is a function
 * next to the constants rather than arithmetic in `main.ts` (D-056, D-059).
 */

import * as THREE from 'three';
import { getAsset } from '@/models/registry';
import { mergeBySurface } from '@/world/instancing';
import { BLACKOUT_OPEN_CRATES, blackoutCratePlacements, blackoutCrateLidHinge } from '@/world/fattening';

/**
 * How far a shoved lid goes over, in radians about the hinge.
 *
 * Not π/2 and not π. A lid thrown fully back lies flat against the crate behind
 * it and reads as "opened by a person"; 2.1 rad leaves it standing at a tilt,
 * which is what a lid shoved from underneath by something small actually does.
 */
const LID_OPEN = 2.1;
/** Seconds for the swing. Fast, because it is a shove and not a mechanism. */
const LID_SWING = 0.38;

export class BlackoutCrates {
  readonly #lids: THREE.InstancedMesh[] = [];
  readonly group: THREE.Group;
  /** Per-crate swing progress, 0…1. Indexed by crate, not by openable crate. */
  readonly #progress: number[];
  readonly #opening: boolean[];

  constructor() {
    const asset = getAsset('hen-crate');
    if (!asset) throw new Error('blackout crates: hen-crate asset is not registered');
    const crate = asset.create();
    const pivot = crate.getObjectByName('hen-crate-lid-pivot');
    if (!pivot) throw new Error('blackout crates: hen-crate lost its lid pivot');
    // Detached so `mergeBySurface` reads the lid's geometry in the PIVOT's own
    // frame — the hinge at the origin — rather than in the crate's. Everything
    // below then composes hinge → swing → placement, with no baked offset to
    // subtract back out.
    pivot.removeFromParent();
    pivot.position.set(0, 0, 0);

    this.group = new THREE.Group();
    this.group.name = 'blackout-hen-crate-lids';

    const placements = blackoutCratePlacements();
    this.#progress = placements.map(() => 0);
    this.#opening = placements.map(() => false);

    for (const batch of mergeBySurface(pivot)) {
      const mesh = new THREE.InstancedMesh(batch.geometry, batch.material, placements.length);
      mesh.name = 'blackout-hen-crate-lid';
      mesh.castShadow = batch.castShadow;
      mesh.receiveShadow = batch.receiveShadow;
      // The instance matrices are rewritten every frame a lid is moving, so the
      // batch's bounds must cover the whole crate row rather than one lid at the
      // origin — otherwise the row is culled the moment the camera turns.
      mesh.frustumCulled = false;
      this.#lids.push(mesh);
      this.group.add(mesh);
    }

    this.#writeMatrices();
  }

  /** Shove the lid of one crate. Idempotent: a second nudge is not a second swing. */
  open(crateIndex: number): void {
    if (this.#opening[crateIndex] === undefined) return;
    this.#opening[crateIndex] = true;
  }

  /** True once a shoved lid has finished travelling. Read by the flock's release. */
  isOpen(crateIndex: number): boolean {
    return (this.#progress[crateIndex] ?? 0) >= 1;
  }

  update(dt: number): void {
    let moved = false;
    for (let index = 0; index < this.#progress.length; index += 1) {
      if (this.#opening[index] !== true) continue;
      const was = this.#progress[index] ?? 0;
      if (was >= 1) continue;
      this.#progress[index] = Math.min(1, was + dt / LID_SWING);
      moved = true;
    }
    if (moved) this.#writeMatrices();
  }

  #writeMatrices(): void {
    const placements = blackoutCratePlacements();
    const matrix = new THREE.Matrix4();
    const hinge = new THREE.Matrix4();
    const swing = new THREE.Matrix4();
    const place = new THREE.Matrix4();

    for (let index = 0; index < placements.length; index += 1) {
      const placement = placements[index];
      if (!placement) continue;
      const openable = BLACKOUT_OPEN_CRATES.findIndex((crate) => crate.index === index);
      // ── The hinge offset is LOCAL, and the first build yawed it twice ──────
      //
      // `place` below already carries the crate's yaw, so pre-rotating this
      // offset and then multiplying by `place` applies the rotation a second
      // time: at rotY = π/2 the offset (0, 0.27, −0.255) came out as
      // (0, 0.27, +0.255) and every lid in the row sat 0.26 m off its own crate
      // in both x and z. It looked like a slightly untidy stack in the frame and
      // was found by measuring the two groups' bounds, not by looking — which is
      // this project's most frequent defect and the reason `instancing.ts` has
      // three paragraphs about the same mistake.
      hinge.makeTranslation(0, 0.27, -0.255);

      const progress = this.#progress[index] ?? 0;
      // Eased, because a lid that arrives at its stop at full speed reads as a
      // lid that hit something. `1 - (1 - t)^3` is the same curve the chapter
      // card walks its strokes on.
      const eased = 1 - (1 - progress) ** 3;
      // The hinge line runs along the crate's local X, which the quarter turn in
      // the placement sends to world Z. Rotating about local X before the yaw is
      // applied keeps this true whichever way a crate is turned.
      swing.makeRotationX(-eased * (openable >= 0 ? LID_OPEN : 0));

      place.compose(
        new THREE.Vector3(placement.x, placement.y ?? 0, placement.z),
        new THREE.Quaternion().setFromEuler(new THREE.Euler(0, placement.rotY ?? 0, 0, 'YXZ')),
        new THREE.Vector3(1, 1, 1),
      );
      matrix.identity().multiply(place).multiply(hinge).multiply(swing);

      for (const mesh of this.#lids) mesh.setMatrixAt(index, matrix);
    }
    for (const mesh of this.#lids) mesh.instanceMatrix.needsUpdate = true;
  }

  /** World position a freed bird steps out of. One number, shared with the flock. */
  static mouthOf(openableIndex: number): { x: number; y: number; z: number } {
    const hinge = blackoutCrateLidHinge(openableIndex);
    // ── Clear of the crate, not merely away from the hinge ──────────────────
    //
    // The hinge is against the hall wall and the crate is 0.55 m deep across the
    // alley, so its far rim is 0.51 m out. 0.42 put the birds INSIDE their own
    // crate's footprint — measured, not eyeballed. 0.66 stands them on the alley
    // a hand's width clear of the rim they just came over.
    return { x: hinge.x + 0.66, y: 0.09, z: hinge.z };
  }

  dispose(): void {
    for (const mesh of this.#lids) {
      mesh.geometry.dispose();
      mesh.removeFromParent();
    }
    this.#lids.length = 0;
    this.group.removeFromParent();
  }
}
